import _                        from 'underscore';
import { getArrColumnTotals }   from '@src/util';
import { Charting }             from '@src/types/charting';
import { LangService }          from '@src/services';
import { useSystemData }        from '@src/hooks/useSystemData';
import { useChartReportExport } from './useChartReportExport';


type ReportChartingType = Charting.Reports.ActivityCostByBusinessFunctionYearlyByProject.ChartingType;
type ReportChartingData = Charting.Reports.ActivityCostByBusinessFunctionYearlyByProject.ChartingData;

type HookType = Charting.Reporting.ChartReportExportHookMethod<ReportChartingType, ReportChartingData>;
type HookOptions = Charting.Reporting.ChartReportExportHookOptions<ReportChartingType>;

type CSVDataRow = {
    project: string,
    year: string,
    total: string,
    [ businessFunctionKey: string ]: string
};
type CSVDataRows = Array<CSVDataRow>;

export const useActivityCostByBusinessFunctionYearlyByProjectChartReportExport: HookType = ( options: HookOptions ) => {

    const defaultHandlers = useChartReportExport( options ),
          systemData      = useSystemData();

    /* Generate text/ string */
    const generateReportText: ReturnType<HookType>['generateReportText'] = ( data ) => {

        let text = [];
        const textData      = generateCSVData( data ),
              groupedByYear = _.groupBy( textData.data, 'year' );

        const allBusinessFunctions = systemData.businessFunctions;

        _.each( groupedByYear, ( yearData, year ) => {

            text.push( `- ${ year }\n` );

            yearData.map( ( row: CSVDataRow ) => {

                text.push( `\t- ${ row.project }: ${ row.total }` );

                allBusinessFunctions.map( ( businessFunctionKey ) => {

                    const businessFunction = _.findWhere( textData.headers, { key: businessFunctionKey } )?.label ?? '(Other)';

                    text.push( `\t\t- ${ businessFunction }: ${ row[ businessFunctionKey ] }` );

                } );

                text.push( `\n` );
            } );

            text.push( `\n` );
        } );

        return text.join( '\n' );
    };

    /* Generate CSV Headers + Data */
    const generateCSVData: ReturnType<HookType>['generateCSVData'] = ( data ) => {

        const years                = data.labels,
              isCompiled           = _.isObject( data.datasets[ 0 ].extendedData.businessFunctionBreakdown ),
              allBusinessFunctions = systemData.businessFunctions;

        // [ Business Function, ...Year Columns, Total ]
        let csvHeaders = [

            { label: "Project", key: "project" },
            { label: "Year", key: "year" },

            ...allBusinessFunctions.map( ( businessFunction ) => ( {
                label: businessFunction.name,
                key:   businessFunction.id,
            } ) ),

            { label: "Total", key: "total" },
        ];

        let csvData: CSVDataRows = [];

        if( isCompiled ){

            data.datasets.map( ( dataset ) => {

                const projectName = dataset.label,
                      totals      = dataset.data;

                // Collect by business function
                const businessFunctionByYear = _.mapObject( dataset.extendedData.businessFunctionBreakdown, ( businessFunctionData ) => {

                    const yearSet = _.object( years, Array( years.length ).fill( 0 ) );

                    return {
                        businessFunction: businessFunctionData.label,
                        data:             years.map( ( year, index ) => yearSet[ year ] = {
                            year,
                            value: businessFunctionData.data[ index ]
                        } )
                    };

                } );

                years.map( ( year, yearIndex ) => {

                    const csvRow = {
                        project: projectName,
                        year:    `${ year }`,
                        total:   options.datapointFormatter( totals[ yearIndex ] ),
                    };

                    allBusinessFunctions.map( ( businessFunctionKey ) => {

                        csvRow[ businessFunctionKey ] = options.datapointFormatter( businessFunctionByYear[ businessFunctionKey ]?.data[ yearIndex ].value ?? 0 );

                    } );

                    csvData.push( csvRow as CSVDataRow );
                } );

            } );

        } else {

            const stackGroups = _.groupBy( data.datasets, 'stack' );

            _.values( stackGroups ).map( ( stackGroup ) => {

                //@ts-ignore
                const { projectName } = stackGroup[ 0 ].extendedData;

                years.map( ( year, yearIndex ) => {

                    const csvRow = {
                        project: projectName,
                        year:    `${ year }`,
                        total:   options.datapointFormatter( getArrColumnTotals( _.pluck( stackGroup, 'data' ) )[ yearIndex ] ),
                    };

                    allBusinessFunctions.map( ( businessFunctionKey ) => {

                        const value = _.find( stackGroup, dataset => dataset.extendedData.businessFunction === businessFunctionKey );

                        csvRow[ businessFunctionKey ] = options.datapointFormatter( value?.data[ yearIndex ] ?? 0 );

                    } );

                    csvData.push( csvRow as CSVDataRow );

                } );
            } );
        }

        // sort by year
        csvData = _.sortBy( csvData, row => `${ row.year }-${ row.project }` );

        return {
            headers:  csvHeaders,
            data:     csvData,
            filename: `Report - ${ options.title }.csv`,
            target:   '_blank',
        };
    };

    return {
        generateReportText,
        generateCSVData,
        handleCopiedtoClipboard: defaultHandlers.handleCopiedtoClipboard,
        handleCSVGenerated:      defaultHandlers.handleCSVGenerated,
        handleSaveAsImage:       defaultHandlers.handleSaveAsImage,
    };
};
